import { Box, Stack, Typography } from '@mui/material';
import DOMPurify from 'dompurify';
import { ColoredAvatar } from 'src/components/ColoredAvatar/colored-avatar';
import { DynamicIcon } from 'src/components/DynamicIcon/dynamic-icon';
import { useCopyToClipboard } from 'src/utils/hooks/use-copy-to-clipboard';
import {
  cardContainerSx,
  copyButtonSx,
  kickerSx,
  labelChipSx,
  noteSx,
} from './client-hover-card.styles';

export interface ClientHoverCardProps {
  name: string;
  kicker?: string;
  email?: string;
  phone?: string;
  labels?: string[];
  note?: string;
}

const InfoRow = ({ icon, value }: { icon: string; value: string }) => {
  const { copyToClipboard } = useCopyToClipboard();

  return (
    <Stack
      direction="row"
      alignItems="center"
      gap="0.4375rem"
      sx={{
        fontSize: '0.71875rem',
        color: (theme) => theme.palette.custom.OV.sys.onSurfaceVariant,
        py: '0.125rem',
        minWidth: 0,
        '&:hover .participant-hover-card-copy': { opacity: 1 },
      }}
    >
      <DynamicIcon name={icon} library="lucide" size={13} sx={{ flexShrink: 0 }} />
      <Typography
        component="span"
        noWrap
        sx={{
          fontSize: 'inherit',
          color: (theme) => theme.palette.custom.OV.sys.onBackground,
          userSelect: 'text',
          minWidth: 0,
        }}
      >
        {value}
      </Typography>
      <Box
        component="button"
        type="button"
        className="participant-hover-card-copy"
        aria-label="Copy"
        onClick={() => copyToClipboard(value)}
        sx={copyButtonSx}
      >
        <DynamicIcon name="copy" library="lucide" size={12} />
      </Box>
    </Stack>
  );
};

export const ClientHoverCard = ({
  name,
  kicker = 'Client',
  email,
  phone,
  labels,
  note,
}: ClientHoverCardProps) => {
  return (
    <Box sx={cardContainerSx}>
      <Stack direction="row" alignItems="center" gap="0.5rem" sx={{ mb: '0.4375rem' }}>
        <ColoredAvatar name={name} sx={{ width: '1.5rem', height: '1.5rem', flexShrink: 0 }} />
        <Typography
          component="span"
          noWrap
          sx={{
            fontSize: '0.78125rem',
            fontWeight: 600,
            color: (theme) => theme.palette.custom.OV.sys.onBackground,
            minWidth: 0,
            flex: 1,
          }}
        >
          {name}
        </Typography>
        <Typography component="span" sx={kickerSx}>
          {kicker}
        </Typography>
      </Stack>

      {!!email && <InfoRow icon="mail" value={email} />}
      {!!phone && <InfoRow icon="phone" value={phone} />}

      {!!labels?.length && (
        <Stack direction="row" flexWrap="wrap" gap="0.25rem" sx={{ mt: '0.3125rem' }}>
          {labels.map((chipLabel) => (
            <Box key={chipLabel} component="span" sx={labelChipSx}>
              {chipLabel}
            </Box>
          ))}
        </Stack>
      )}

      {!!note && (
        <Typography
          sx={noteSx}
          // Client notes are stored as rich-text HTML with no plain-text counterpart, so a
          // React tree can't render them; DOMPurify.sanitize (already a dependency, same call
          // as policy-card.tsx) strips scripts/handlers before they reach the DOM.
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(note, { USE_PROFILES: { html: true } }),
          }}
        />
      )}
    </Box>
  );
};
